import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Mail, Lock, User, Phone, Eye, EyeOff, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import MobileBottomNav from '../components/MobileBottomNav';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const RegisterPage = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();
  const { t } = useLanguage();
  const { theme } = useTheme();
  const currentLogo = theme === 'light' 
    ? "https://customer-assets.emergentagent.com/job_9d222022-9462-4aac-aca8-59da15d4f381/artifacts/tueqptuk_registan%20logo.png"
    : "https://customer-assets.emergentagent.com/job_422444ca-8f0f-4ebc-8f78-bee66ce7f723/artifacts/qo6wuzuf_REGISTAN%20LOGO.png";
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    mobile: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Redirect if already logged in
  React.useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/register`, formData);
      
      // Use AuthContext to save login state
      login(response.data.user, response.data.token);
      
      // Redirect to dashboard
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <Helmet>
        <title>Register - Create Account | Registan Tourism UAE</title>
        <meta name="description" content="Create your free Registan Tourism account. Register to book Dubai visas, track applications, and access exclusive travel services in the UAE." />
        <meta name="keywords" content="register registan tourism, create visa account, dubai visa registration, UAE visa account" />
        <link rel="canonical" href="https://registantours.com/register" />
        <meta name="robots" content="index, follow" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://registantours.com/register" />
        <meta property="og:title" content="Register - Registan Tourism UAE" />
        <meta property="og:description" content="Create your free account to book Dubai visas, track applications, and access travel services." />
        <meta property="og:site_name" content="Registan Tourism" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Register - Registan Tourism UAE" />
        <meta name="twitter:description" content="Create your free account to book Dubai visas and track applications." />
      </Helmet>
      <button className="auth-close-btn" onClick={() => navigate('/')}>
        <X size={24} />
      </button>
      <div className="auth-container">
        <div className="auth-header">
          <img src={currentLogo} alt="Registan" className="auth-logo-img" />
          <h1>{t('createAccount')}</h1>
          <p>{t('joinRegistan')}</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {error && (
            <div className="auth-error">
              {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="name">{t('fullName')} *</label>
            <div className="input-with-icon">
              <User size={20} />
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder={t('enterFullName')}
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="email">{t('emailAddress')} *</label>
            <div className="input-with-icon">
              <Mail size={20} />
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="your.email@example.com"
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="mobile">{t('mobileNumber')} ({t('optional')})</label>
            <div className="input-with-icon">
              <Phone size={20} />
              <input
                type="tel"
                id="mobile"
                name="mobile"
                value={formData.mobile}
                onChange={handleChange}
                placeholder="+971 XX XXX XXXX"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="password">{t('password')} *</label>
            <div className="input-with-icon">
              <Lock size={20} />
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder={t('minChars')}
                minLength={6}
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <button 
            type="submit" 
            className="btn-primary btn-auth"
            disabled={loading}
          >
            {loading ? t('creatingAccount') : t('createAccount')}
          </button>
        </form>

        <div className="auth-footer">
          <p>{t('alreadyHaveAccount')} <Link to="/login">{t('signIn')}</Link></p>
          <p style={{ marginTop: '12px', fontSize: '12px', opacity: 0.7 }}>
            <Link to="/privacy" style={{ color: 'inherit' }} data-testid="auth-privacy-link">{t('privacyPolicy') || 'Privacy Policy'}</Link>
          </p>
        </div>
      </div>
      
      {/* Mobile Bottom Navigation */}
      <MobileBottomNav />
    </div>
  );
};

export default RegisterPage;
